import { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { Home, Eye, EyeOff, ArrowLeft, Check, X } from "lucide-react";
import { Link, useLocation } from "wouter";
import Header from "@/components/Header";
import GoogleSign from "./GoogleSign";
import axios from "axios";
import API_URL from "@/config";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import useDocumentTitle from "@/components/Helmet";
import { useSelector } from "react-redux";



const registerSchema = z.object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.string().email("Please enter a valid email address"),
    password: z.string()
        .min(8, "Password must be at least 8 characters")
        .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
        .regex(/[a-z]/, "Password must contain at least one lowercase letter")
        .regex(/[0-9]/, "Password must contain at least one number")
        .regex(/[^A-Za-z0-9]/, "Password must contain at least one special character"),
    confirmPassword: z.string(),
    userType: z.string().min(1, "Please select a user type"),
    acceptTerms: z.boolean().refine((val) => val, "Please accept the terms"),
}).refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
});

type RegisterForm = z.infer<typeof registerSchema>;

const Register = () => {
    useDocumentTitle("Register");
    const { isAuthenticated } = useSelector((state: any) => state.auth);
    const [location, navigate] = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const { toast } = useToast();

    useEffect(() => {
        if (isAuthenticated) navigate("/");
    }, [isAuthenticated])

    const registerForm = useForm<RegisterForm>({
        resolver: zodResolver(registerSchema),
        defaultValues: {
            firstName: "",
            lastName: "",
            email: "",
            password: "",
            confirmPassword: "",
            userType: "",
            acceptTerms: false,
        },
    });

    const [passwordValidation, setPasswordValidation] = useState({
        length: false,
        uppercase: false,
        number: false,
    });

    const validatePasswordRequirements = (password: string) => {
        setPasswordValidation({
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            number: /[0-9]/.test(password),
        });
    };

    const watchedPassword = registerForm.watch("password");
    useEffect(() => {
        validatePasswordRequirements(watchedPassword || "");
    }, [watchedPassword]);

    const handleRegister = async (data: RegisterForm) => {
        setIsLoading(true);
        try {
            await axios.post(API_URL + "/api/auth/register", data)
                .then((res) => {
                    if (res?.data?.success) {
                        toast({
                            title: "Registration successful",
                            description: res?.data?.message || "Please check your email for verification.",
                            variant: "success",
                        });

                        navigate("/login")

                    }
                    else {
                        toast({
                            title: "Registration failed",
                            description: res?.data?.message,
                            variant: "destructive",
                        });
                    }
                })

        } catch (error: any) {
            toast({
                title: "Registration failed",
                description: error?.response?.data?.message || "Please try again later.",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };
    return (
        <div className="min-h-screen bg-gray-50">
            <Header />
            {/* Auth Form */}
            <div className="flex-1 flex items-center justify-center py-2 px-4 sm:px-6 lg:px-8" style={{
                backgroundImage: "url('/background.jpg')",
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
                minHeight: "92vh"
            }}>

                <div className="max-w-xl w-full space-y-8">
                    <Card className="bg-white rounded-2xl shadow-xl auth-transition">
                        <form onSubmit={registerForm.handleSubmit(handleRegister)}>
                            <CardHeader className="text-center pb-6">
                                <CardTitle className="text-3xl font-bold text-gray-900">Create Account</CardTitle>
                                <CardDescription className="text-gray-600">
                                    Join PropertyHub to find your perfect home
                                </CardDescription>
                            </CardHeader>

                            <CardContent className="space-y-4">
                                {/* Social Registration */}
                                <div className="space-y-1">
                                    <GoogleSign />
                                </div>

                                <div className="relative">
                                    <div className="absolute inset-0 flex items-center">
                                        <Separator className="w-full" />
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className="px-2 bg-white text-gray-500">Or sign up with email</span>
                                    </div>
                                </div>

                                {/* Name Fields */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <Label htmlFor="first-name">First name</Label>
                                        <Input
                                            id="first-name"
                                            placeholder="First name"
                                            className="form-input"
                                            data-testid="input-firstname"
                                            {...registerForm.register("firstName")}
                                        />
                                        {registerForm.formState.errors.firstName && (
                                            <p className="text-sm text-red-600">{registerForm.formState.errors.firstName.message}</p>
                                        )}
                                    </div>
                                    <div className="space-y-1">
                                        <Label htmlFor="last-name">Last name</Label>
                                        <Input
                                            id="last-name"
                                            placeholder="Last name"
                                            className="form-input"
                                            data-testid="input-lastname"
                                            {...registerForm.register("lastName")}
                                        />
                                        {registerForm.formState.errors.lastName && (
                                            <p className="text-sm text-red-600">{registerForm.formState.errors.lastName.message}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Email Input */}
                                <div className="space-y-1">
                                    <Label htmlFor="register-email">Email address</Label>
                                    <Input
                                        id="register-email"
                                        type="email"
                                        placeholder="Enter your email"
                                        className="form-input"
                                        data-testid="input-register-email"
                                        {...registerForm.register("email")}
                                    />
                                    {registerForm.formState.errors.email && (
                                        <p className="text-sm text-red-600">{registerForm.formState.errors.email.message}</p>
                                    )}
                                </div>

                                {/* Password Input */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <Label htmlFor="register-password">Password</Label>
                                        <div className="relative">
                                            <Input
                                                id="register-password"
                                                type={showPassword ? "text" : "password"}
                                                placeholder="Create a password"
                                                className="form-input pr-12"
                                                data-testid="input-register-password"
                                                {...registerForm.register("password")}
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                                onClick={() => setShowPassword(!showPassword)}
                                                data-testid="button-toggle-register-password"
                                            >
                                                {showPassword ? (
                                                    <EyeOff className="h-4 w-4 text-gray-400" />
                                                ) : (
                                                    <Eye className="h-4 w-4 text-gray-400" />
                                                )}
                                            </Button>
                                        </div>

                                        {/* Password Requirements */}
                                        {/* <div className="mt-2 text-sm text-gray-600">
                                        <p className="mb-1">Password must contain:</p>
                                        <ul className="space-y-1 text-xs">
                                            <li className="flex items-center">
                                                {passwordValidation.length ? (
                                                    <Check className="w-3 h-3 text-green-500 mr-2" />
                                                ) : (
                                                    <X className="w-3 h-3 text-red-500 mr-2" />
                                                )}
                                                At least 8 characters
                                            </li>
                                            <li className="flex items-center">
                                                {passwordValidation.uppercase ? (
                                                    <Check className="w-3 h-3 text-green-500 mr-2" />
                                                ) : (
                                                    <X className="w-3 h-3 text-red-500 mr-2" />
                                                )}
                                                One uppercase letter
                                            </li>
                                            <li className="flex items-center">
                                                {passwordValidation.number ? (
                                                    <Check className="w-3 h-3 text-green-500 mr-2" />
                                                ) : (
                                                    <X className="w-3 h-3 text-red-500 mr-2" />
                                                )}
                                                One number
                                            </li>
                                        </ul>
                                    </div> */}
                                        {registerForm.formState.errors.password && (
                                            <p className="text-sm text-red-600">{registerForm.formState.errors.password.message}</p>
                                        )}
                                    </div>

                                    {/* Confirm Password */}
                                    <div className="space-y-1">
                                        <Label htmlFor="confirm-password">Confirm password</Label>
                                        <div className="relative">
                                            <Input
                                                id="confirm-password"
                                                type={showConfirmPassword ? "text" : "password"}
                                                placeholder="Confirm your password"
                                                className="form-input pr-12"
                                                data-testid="input-confirm-password"
                                                {...registerForm.register("confirmPassword")}
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                data-testid="button-toggle-confirm-password"
                                            >
                                                {showConfirmPassword ? (
                                                    <EyeOff className="h-4 w-4 text-gray-400" />
                                                ) : (
                                                    <Eye className="h-4 w-4 text-gray-400" />
                                                )}
                                            </Button>
                                        </div>
                                        {registerForm.formState.errors.confirmPassword && (
                                            <p className="text-sm text-red-600">{registerForm.formState.errors.confirmPassword.message}</p>
                                        )}
                                    </div>
                                </div>

                                <div>
                                    <Controller
                                        name="userType"
                                        control={registerForm.control}
                                        defaultValue=""
                                        render={({ field }) => (
                                            <div className="space-y-1">
                                                <Label htmlFor="user-type">Are you?</Label>
                                                <Select value={field.value} onValueChange={(val) => field.onChange(val)}>
                                                    <SelectTrigger className="text-gray-900 text-sm" data-testid="select-user-type" id="user-type">
                                                        <SelectValue placeholder="Are you a Seller, Buyer, or Agent?" />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="seller">Seller</SelectItem>
                                                        <SelectItem value="buyer">Buyer</SelectItem>
                                                        <SelectItem value="agent">Agent</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                        )}
                                    />
                                    {registerForm.formState.errors.userType && (
                                        <p className="text-sm text-red-600">{registerForm.formState.errors.userType.message}</p>
                                    )}
                                </div>

                                {/* Terms and Privacy */}
                                <div className=" space-x-2">
                                    {/* Terms and Privacy */}
                                    <Controller
                                        name="acceptTerms"
                                        control={registerForm.control}
                                        render={({ field }) => (
                                            <div className="flex items-start space-x-2">
                                                <Checkbox
                                                    id="terms"
                                                    checked={field.value}
                                                    onCheckedChange={(checked) => field.onChange(checked)}
                                                    data-testid="checkbox-terms"
                                                />
                                                <Label htmlFor="terms" className="text-sm text-gray-600 leading-5">
                                                    I agree to the{" "}
                                                    <a href="#" className="text-primary hover:text-primary-600">Terms of Service</a>
                                                    {" "}and{" "}
                                                    <a href="#" className="text-primary hover:text-primary-600">Privacy Policy</a>
                                                </Label>
                                            </div>
                                        )}
                                    />
                                    {registerForm.formState.errors.acceptTerms && (
                                        <p className="text-sm text-red-600">{registerForm.formState.errors.acceptTerms.message}</p>
                                    )}
                                </div>

                                {/* Submit Button */}
                                <Button
                                    type="submit"
                                    className="w-full btn-hover"
                                    disabled={isLoading}
                                    data-testid="button-create-account"
                                >
                                    {isLoading ? "Creating account..." : "Create Account"}
                                </Button>

                                {/* Switch to Login */}
                                <p className="text-center text-sm text-gray-600">
                                    Already have an account?{" "}
                                    <Button
                                        type="button"
                                        variant="link"
                                        className="text-primary hover:text-primary-600 p-0"
                                        onClick={() => navigate("/login")}
                                        data-testid="button-switch-login"
                                    >
                                        Sign in
                                    </Button>
                                </p>
                            </CardContent>
                        </form>
                    </Card>
                </div>
            </div>
        </div>
    )
}

export default Register